/**
 * pages/hazard/Register.js — FR-04-01 Аюулын мэдээлэл бүртгэх (refactored).
 *
 * HazardFormComposer-ыг ашигладаг — UI section-уудыг component-аар хуваасан.
 * Mode-аас хамаарч ямар section-ууд харагдах нь [[hazardModes]]-аас тогтооно.
 *
 * Энэ хуудсанд эрхээс үл хамааран БҮХ хэрэглэгч (наряд авсан/аваагүй ажилтан,
 * ямар ч эрхтэй хүн) "Аюул мэдээлэх" анхны маягтаа авна. ХАБ хэлтсийн
 * мэргэжилтний зөрчил/үзлэгийн бүртгэл нь /incident/register хуудсанд явагдана.
 *
 * Mode сонгох:
 *   1. URL query `?mode=stop_work` (prework15-аас гэх мэт)
 *   2. Өнөөдөр ӨЭА авсан → oea
 *   3. Бусад            → naryad
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useGoBack from "src/hooks/useGoBack";
import { useUserContext } from "src/contexts/userContext";
import { Radio } from "antd";
import Swal from "sweetalert2";

import HazardFormComposer, {
  getModeConfig,
  PAGE_BG,
  RED,
  INSPECTION_TYPES,
} from "src/components/hazard";
import PageHelp from "src/components/help/PageHelp";
import {
  getCategories,
  getSeverities,
  createHazard,
  uploadHazardPhoto,
  getMyOeaRegistration,
} from "src/api/hazard";

const DRAFT_KEY = "hazard_register_draft";

/**
 * Өнөөдрийн огноо `YYYY-MM-DD` (орон нутгийн).
 *
 * ⚠ `toISOString()` хэрэглэхгүй — тэр нь UTC руу хөрвүүлдэг тул
 *   Улаанбаатарт (UTC+8) орой 16:00-аас хойш ӨМНӨХ өдрийг буцаана.
 *   ӨЭА бүртгэл нь орон нутгийн ажлын өдрөөр тавигддаг.
 */
function todayYmd() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/* ── МАРШРУТ → ХЭНД МЭДЭГДЭЛ ОЧИХ (2026-11-01, хэрэглэгч) ──────────────
   «БН шийдэнэ гэж илгээсэн нөхцөлд анхааруулга дээр нь БН-н хаб н
   мэргэжилтэнд илгээгдэнэ гэсэн анхааруулга гаргах, хаб хэлтэс
   шийдвэрлэнэ гэсэн үед … БН хаб болон БН н удирдлага, хаб хэлтэсийн
   мэргэжилтэнд гэсэн байдалтай».

   ⚠ ТЕКСТ НЬ СЕРВЕРТЭЙ ТААРАХ ЁСТОЙ: `Utils/notify.py ::
     resolve_recipients` нь `route_choice`-оор хүлээн авагчийг шийднэ
     (unit → бүлэг 179; hseh → 179 + 166·192 + notification_recipient).
     Зөрвөл хэрэглэгч хэнд явснаа буруу мэдэж, хүлээж гацна.
   ⚠ НЭГ ГАЗАР: сонголтын доорх санамж БА бүртгэсний дараах цонх
     хоёулаа ҮҮНИЙГ дуудна. Хуулбарлавал хоёр нь зөрнө. */
const ROUTE_RECIPIENTS = {
  unit: "тухайн БН-ийн ХАБ мэргэжилтэнд",
  hseh:
    "БН-ийн ХАБ мэргэжилтэн, БН-ийн удирдлага болон " +
    "ХАБ хэлтсийн мэргэжилтэнд",
};

/* Сонгоогүй үед сервер нь анхдагч хүрээ (хэсгийн дарга · цехийн дарга ·
   ХАБ хэлтэс) рүү явуулна. */
const ROUTE_DEFAULT_RECIPIENTS =
  "хэсгийн дарга, цехийн дарга болон ХАБ хэлтэст";

const routeRecipientText = (rc) =>
  ROUTE_RECIPIENTS[rc] || ROUTE_DEFAULT_RECIPIENTS;

const Register = () => {
  const navigate = useNavigate();
  const { checkRole } = useUserContext();
  const location = useLocation();
  // Буцах — ирсэн хуудас руу. `state.from` (ж: /me) → апп доторх түүх →
  // анхдагч. Логик нь дундын hook-д (src/hooks/useGoBack.js).
  //
  // ⚠ Анхдагчийг эрхээр сонгоно: аюулын ХЯНАХ САМБАР нь `hazard_dashboard`
  // эрхтэй хүнд л зориулагдсан. Энгийн ажилтныг тийш шидвэл хоосон/хориотой
  // хуудсанд гарна. Тиймээс тэдэнд «Миний ХЭМАБ» (/me) руу буцаана.
  const canSeeHazardDashboard = !!(
    checkRole && checkRole(["hazard_dashboard"])
  );
  // Энэ хуудас ЦЭСНЭЭС ч нээгддэг тул түүхэнд найдахгүй — эс бөгөөс
  // хэрэглэгч /inspection/register дээр байгаад цэснээс «Аюул бүртгэх»
  // сонговол буцахдаа тэр хуудас руу орчихно. Эх хаягийг дуудагч нь
  // `state.from`-оор дамжуулна.
  const goBack = useGoBack(
    canSeeHazardDashboard ? "/hazard/dashboard" : "/me",
    { useHistory: false },
  );

  // ── Mode тогтоох ──────────────────────────────────────────────────────────
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const queryMode = queryParams.get("mode");

  const [oeaToday, setOeaToday] = useState(null);
  const [modeLoading, setModeLoading] = useState(true);

  // ХАБ хэлтсийн мэргэжилтэн / БН ХАБ зэрэг эрхээс үл хамааран энэ хуудсанд орсон
  // БҮХ хэрэглэгч "Аюул мэдээлэх" анхны маягтаа авна. Тэдгээрийн зөрчил/үзлэг
  // бүртгэл нь /incident/register хуудсанд явагдана.
  const mode = useMemo(() => {
    if (queryMode && getModeConfig(queryMode)) return queryMode;
    if (oeaToday) return "oea";
    return "naryad";
  }, [queryMode, oeaToday]);

  const cfg = getModeConfig(mode);

  /* ── МАРШРУТЫН СОНГОЛТ ЗӨВХӨН АЮУЛ МЭДЭЭЛЭХ ГОРИМД ─────────────
     Хэмжсэн: дээрх `mode` нь query-гүй үед ҮРГЭЛЖ `naryad`
     (ӨЭА-тай бол `oea`) буудаг — бүх хэрэглэгч энэ хуудсанд «Аюул
     мэдээлэх» маягтаа авдаг. Зөрчил/үзлэг нь `/incident/register`.
     ⚠ Тиймээс зөрчлийн горимуудад (bn_violation, hab_dept_violation,
       inspection_full, stop_work …) сонголт ГАРАХГҮЙ: тэдгээрт эзэн
       нь процессоор аль хэдийн тодорсон байдаг. */
  const showRoute = mode === "naryad" || mode === "oea";

  // ── Lookup ─────────────────────────────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [severities, setSeverities] = useState([]);

  // ── Form value ─────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    photos: [],
    deptCode: "",
    deptParent: null,
    deptName: "",
    sectionCode: "",
    sectionName: "",
    locationText: "",
    locationDescription: "",
    /* ── МАРШРУТЫН СОНГОЛТ (2026-10-28, хэрэглэгчийн хүсэлт) ──────
       «Ажилтан аюулын мэдээлсэн тохиолдолд үйлдвэрийн газрын
        ХАБЭАХ-рүү хуваарилах эсвэл бүтцийн нэгж дотроо шийдэх
        асуудал уу гэдэг сонголттой байх.»
       null = сонгоогүй (хуучин зан төлөв — ХАБ хэлтэс шийднэ). */
    routeChoice: null,
    gps: { lat: null, lng: null, accuracy: null },
    categoryId: null,
    categoryIds: [],
    hazardId: null,
    description: "",
    voiceFileUrl: "",
    severity: 2,
    affectedLocation: "",
    peopleCount: "",
    selfInDanger: false,
    date: null,
    inspectionLevel: cfg.defaultInspectionLevel ?? null,
    inspectionType: null,
    // Мэдээллийн ангилал — лавлахаас (xab.violation_reference/info_class).
    // `*_text` нь лавлах өөрчлөгдсөн ч бүртгэл дээрх утга уншигдахаар
    // хамт хадгалагдана (денормал хуулбар).
    info_class_id: null,
    info_class_text: null,
  });

  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── ӨЭА шалгалт (mode тооцоолоход) ────────────────────────────────────────
  //
  // ⚠ ОГНООГ ЗААВАЛ ДАМЖУУЛНА. `getMyOeaRegistration()` нь огноогүй үед
  //   backend дээр `work_date` шүүлт ОГТ тавигддаггүй (oea_worker.py) тул
  //   хэрэглэгчийн ХАМГИЙН СҮҮЛИЙН ӨЭА бүртгэл — ямар ч огнооных — буцдаг.
  //
  //   Улмаас НЭГ УДАА ӨЭА бүртгүүлсэн хүн энэ хуудсанд ҮҮРД `oea` горимд
  //   гацдаг байв: `oea` горимд «Мэдээллийн ангилал» хэсэг байхгүй тул
  //   аюулаа мэдээлэхэд ангиллаа сонгох боломжгүй болдог. 7 хэрэглэгч
  //   ийм байдалд орсон байсан (2026-09-16-нд илрүүлсэн).
  //
  // ⚠ Зөвхөн ЭНД засав — `getMyOeaRegistration`-ийн анхдагчийг өөрчилвөл
  //   5А/15А урьдчилсан шалгалтын хуудсуудын зан төлөв хамт өөрчлөгдөнө.
  useEffect(() => {
    if (queryMode) {
      setModeLoading(false);
      return;
    }
    let mounted = true;
    getMyOeaRegistration({ work_date: todayYmd() })
      .then((r) => mounted && setOeaToday(r || null))
      .catch(() => mounted && setOeaToday(null))
      .finally(() => mounted && setModeLoading(false));
    return () => {
      mounted = false;
    };
  }, [queryMode]);

  // ── Lookup + ноорог сэргээх ──────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [cat, sev] = await Promise.all([getCategories(), getSeverities()]);
        setCategories(cat.items || []);
        setSeverities(sev.items || []);
      } catch {
        /* offline ч ажиллана */
      }
    })();
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        setForm((prev) => ({ ...prev, ...d }));
      }
    } catch {
      /* draft уншсангүй */
    }
  }, []);

  // ── Зураг upload ─────────────────────────────────────────────────────────
  // Зөвхөн upload хийж { file_url, file_name } буцаана. form.photos-д нэмэхийг
  // PhotoSection (onPhotosChange) хариуцна — давхар нэмэхээс сэргийлнэ.
  const onPickFile = async (file) => {
    if (!file) return null;
    setUploading(true);
    try {
      const r = await uploadHazardPhoto(file);
      return { file_url: r.file_url, file_name: r.file_name };
    } catch {
      Swal.fire({ title: "Зураг хадгалж чадсангүй", icon: "error" });
      return null;
    } finally {
      setUploading(false);
    }
  };

  // ── Ноорог хадгалах ──────────────────────────────────────────────────────
  const onDraft = () => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      Swal.fire({
        title: "Ноорог хадгалагдлаа",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({ title: "Ноорог хадгалж чадсангүй", icon: "error" });
    }
  };

  // ── Илгээх ────────────────────────────────────────────────────────────────
  const onSave = async () => {
    const sections = cfg.sections || [];
    const hasCategory = sections.includes("hazardType");
    const hasSeverity = sections.includes("severity");
    // Мэдээллийн ангилал нь ЗӨВХӨН тухайн горимын маягтад байвал шаардана
    const hasInfoClass = sections.includes("infoClass");

    const categoryIds =
      form.categoryIds && form.categoryIds.length
        ? form.categoryIds
        : form.categoryId != null
          ? [form.categoryId]
          : [];
    if (hasCategory && categoryIds.length === 0) {
      Swal.fire({ title: "Аюулын төрлийг сонгоно уу", icon: "warning" });
      return;
    }
    if (!form.description.trim()) {
      Swal.fire({ title: "Тайлбарыг бичнэ үү", icon: "warning" });
      return;
    }
    // ⚠ Зөвхөн уг хэсэг МАЯГТАД БАЙГАА горимд шаардана. Бүх горимд
    //   шаардвал зөрчил/ӨЭА маягтууд шалтгаангүйгээр хаагдана.
    if (hasInfoClass && !form.info_class_id) {
      Swal.fire({ title: "Мэдээллийн ангилалаа сонгоно уу", icon: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await createHazard({
        location_text: form.locationText,
        location_description: form.locationDescription || undefined,
        gps_lat: form.gps?.lat ?? undefined,
        gps_lng: form.gps?.lng ?? undefined,
        gps_accuracy: form.gps?.accuracy ?? undefined,
        hazard_category_id:
          hasCategory && categoryIds.length ? Number(categoryIds[0]) : undefined,
        // 1-2 аюулын төрөл — таслалаар (эхнийх нь hazard_category_id-тэй ижил)
        hazard_category_ids:
          hasCategory && categoryIds.length
            ? categoryIds.map(Number).join(",")
            : undefined,
        hazard_id: form.hazardId || undefined,
        description: form.description.trim(),
        /* ⚠ ЗӨВХӨН аюул мэдээлэх горимд. Зөрчил/үзлэгийн маягтад
           (`/incident/register`) эзэн нь процессоор тодордог тул
           сонголт утгагүй. Backend ч танихгүй утгыг NULL болгоно. */
        route_choice: showRoute ? form.routeChoice || undefined : undefined,
        voice_file_url: form.voiceFileUrl || undefined,
        /* ⚠ САНААТАЙ: `naryad` горимд эрсдлийн түвшний сонголт БАЙХГҮЙ тул
           анхдагч 2 (Дунд) явна. Энэ нь алдаа БИШ — 2026-08-11-нд гаргасан
           шийдвэр: энгийн ажилтан зөвхөн мэдээлнэ, түвшнийг ХАБ мэргэжилтэн
           хянах шатанд тогтооно (маягтыг богино байлгаж, мэдээлэх саадыг
           бууруулах зорилготой).

           ⚠ ҮР ДАГАВРЫГ МЭДЭЖ БАЙХ: SLA нь түвшнээс бодогддог
           (Models/mobile/hazard_report.py → SEVERITY_SLA_MIN):
               1 Бага 7 хоног · 2 Дунд 3 хоног · 3 Өндөр 4 цаг
           Тиймээс ХАБ хянах хүртэл ШУУД АЮУЛ ч 3 хоногийн хугацаатай
           бүртгэгдэнэ. Хяналтын дараалал удаашрах нь эрсдэл болно. */
        severity: hasSeverity ? form.severity : 2,
        report_mode: mode,
        affected_location: form.affectedLocation || form.locationText,
        affected_people_count: form.peopleCount || undefined,
        self_in_danger: form.selfInDanger,
        photos: form.photos,
        inspection_level: form.inspectionLevel ?? undefined,
        inspection_type: form.inspectionType ?? undefined,
        // string key-ээс баз дахь id-г хайж илгээнэ (hydrate хийсний дараа id-тэй)
        inspection_type_id:
          INSPECTION_TYPES.find((t) => t.key === form.inspectionType)?.id ??
          undefined,
        report_date: form.date || undefined,
        // Лавлахаас сонгосон ангилал — backend `_apply_vclass`-аар
        // `xab.hazard_reports.info_class_id/_text` рүү бичигдэнэ.
        info_class_id: form.info_class_id ?? undefined,
        info_class_text: form.info_class_text ?? undefined,
        offline_client_id:
          "web-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8),
      });
      localStorage.removeItem(DRAFT_KEY);
      const notified = res?.notified || {};
      // Мэдэгдэл нь backend дээр арын thread-д явдаг тул хариу ирэх үед хүлээн
      // авагчийн тоо хараахан тодорхойгүй — тоогүйгээр мэдээлнэ.
      const recipientsTxt = notified.queued
        ? `<br/><span style="font-size:12px;color:#1B7F3B">Холбогдох удирдлагад мэдэгдэл илгээгдэж байна.</span>`
        : "";
      await Swal.fire({
        title: "Амжилттай бүртгэгдлээ",
        html:
          (res.report_number
            ? `<div style="font-size:14px;margin-bottom:8px">Дугаар: <b>${res.report_number}</b></div>`
            : "") +
          `<div style="font-size:13px;color:#1E3A8A;background:#EFF6FF;border-left:3px solid #2563EB;padding:8px 10px;border-radius:6px;text-align:left">
            <i class="fas fa-paper-plane" style="color:#2563EB;margin-right:6px"></i>
            Таны мэдээлэл <b>${routeRecipientText(form.routeChoice)}</b>
            <b style="color:#DC2626">3–5 секундын дотор</b> илгээгдлээ.${recipientsTxt}
          </div>`,
        icon: "success",
      });
      goBack();
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Илгээхэд алдаа гарлаа";
      Swal.fire({ title: "Илгээхэд алдаа гарлаа", text: msg, icon: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = goBack;

  if (modeLoading) {
    return (
      <div
        style={{
          background: PAGE_BG,
          minHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ color: "#8A94A6", fontSize: 13 }}>Уншиж байна...</div>
      </div>
    );
  }

  return (
    <div style={{ background: PAGE_BG, minHeight: "100%" }}>
      <style>{`
        .hz-wrap { max-width: 480px; margin: 0 auto; min-height: 100%; background: ${PAGE_BG}; }
        @media (min-width: 900px) {
          .hz-wrap { max-width: 100%; }
          .hz-grid { column-count: 2; column-gap: 14px; }
          /* Багана хооронд элемент хуваагдвал доторх textarea/input
             Chrome/Safari дээр focus авахгүй болдог тул хуваахыг хориглоно. */
          .hz-grid > * { break-inside: avoid; -webkit-column-break-inside: avoid; }
        }
        @media (min-width: 1400px) {
          .hz-grid { column-count: 3; }
        }
      `}</style>
      <div className="hz-wrap">
        {/* Header */}
        <div
          style={{
            background: RED,
            color: "#fff",
            padding: "14px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <button
            type="button"
            onClick={() => goBack()}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: 18,
              cursor: "pointer",
              padding: 4,
            }}
            title="Буцах"
          >
            <i className="fas fa-arrow-left" />
          </button>
          <span
            style={{
              fontSize: 16,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {cfg.title}
            <PageHelp light />
          </span>
          <button
            type="button"
            onClick={() => goBack()}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: 18,
              cursor: "pointer",
              padding: 4,
            }}
            title="Жагсаалт"
          >
            <i className="fas fa-list" />
          </button>
        </div>

        <div style={{ padding: 14 }}>
          <div className="hz-grid">
            {/* ── ХЭН ШИЙДВЭРЛЭХ ВЭ (2026-10-28) ───────────────────
                ⚠ Композерын section системийг ХӨНДӨӨГҮЙ: тэр нь 9
                  горимын тохиргоог дамждаг тул нэг section нэмэхэд
                  бүх горимд үйлчилнэ. Энэ сонголт зөвхөн аюул
                  мэдээлэхэд хамаарна. */}
            {showRoute ? (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 10,
                  padding: "12px 14px",
                  marginBottom: 10,
                  border: "1px solid #E6E9EF",
                }}
              >
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    marginBottom: 7,
                    color: "#1F2430",
                  }}
                >
                  Хэн шийдвэрлэх вэ?
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: 11,
                      color: "#8A94A6",
                      marginLeft: 6,
                    }}
                  >
                    (заавал биш)
                  </span>
                </div>
                <Radio.Group
                  value={form.routeChoice || ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      routeChoice: e.target.value || null,
                    }))
                  }
                >
                  <Radio value="unit">Бүтцийн нэгж дотроо шийднэ</Radio>
                  <Radio value="hseh">
                    Үйлдвэрийн газрын ХАБЭАХ-руу
                  </Radio>
                </Radio.Group>
                {/* Сонгосон даруйд ХЭНД очихыг хэлнэ — илгээсний
                    дараа «хэнд явсан бол» гэж тээнэгэлзэхгүй. */}
                <div
                  style={{
                    fontSize: 11,
                    marginTop: 6,
                    color: form.routeChoice ? "#1B7F3B" : "#A8AFBC",
                  }}
                >
                  {form.routeChoice ? (
                    <>
                      <i
                        className="fas fa-paper-plane"
                        style={{ marginRight: 5 }}
                      />
                      Мэдэгдэл {routeRecipientText(form.routeChoice)}{" "}
                      илгээгдэнэ.
                    </>
                  ) : (
                    "Сонгохгүй бол ХАБ хэлтэс хариуцах нэгжийг тодорхойлно."
                  )}
                </div>
              </div>
            ) : null}
            <HazardFormComposer
              mode={mode}
              value={form}
              onChange={setForm}
              categories={categories}
              severities={severities}
              onPickFile={onPickFile}
              uploading={uploading}
              onSave={onSave}
              onDraft={onDraft}
              onCancel={onCancel}
              saving={submitting}
            />
          </div>
          <div style={{ height: 20 }} />
        </div>
      </div>
    </div>
  );
};

export default React.memo(Register);
